import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import useAuth from "@/hooks/useAuth";
import propertiesApi from "@/api/propertiesApi";
import useAmenityCatalog from "@/hooks/useAmenityCatalog";
import BookingsPanel from "@/components/BookingsPanel/BookingsPanel";
import CalendarSyncPanel from "@/components/CalendarSyncPanel/CalendarSyncPanel";
import LocationPicker from "@/components/LocationPicker/LocationPicker";
import PropertyReviewsPanel from "@/components/PropertyReviewsPanel/PropertyReviewsPanel";
import ApiError from "@/model/ApiError";
import PropertyResponse from "@/model/PropertyResponse";
import propertyListPath from "@/routing/propertyListPath";
import formatPrice from "@/utils/formatPrice";
import "@/pages/PropertyDetailsPage/PropertyDetailsPage.scss";

function PropertyDetailsPage() {
  const { t } = useTranslation();
  const { session } = useAuth();
  const { id } = useParams<{ id: string }>();
  const amenityCatalog = useAmenityCatalog();

  const [property, setProperty] = useState<PropertyResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<"notFound" | "generic" | null>(null);

  useEffect(() => {
    if (!session || !id) {
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setError(null);

    propertiesApi
      .getPropertyById(session.accessToken, id)
      .then((result) => {
        if (!cancelled) {
          setProperty(result);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof ApiError && err.status === 404 ? "notFound" : "generic");
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [session, id]);

  if (!session || !id) {
    return null;
  }

  const address = property?.address;
  const offered = new Set(property?.amenities ?? []);
  const amenityGroups = amenityCatalog.categories
    .map((category) => ({
      ...category,
      amenities: category.amenities.filter((amenity) => offered.has(amenity.code)),
    }))
    .filter((category) => category.amenities.length > 0);
  const location =
    address?.latitude !== undefined && address?.longitude !== undefined
      ? { latitude: address.latitude, longitude: address.longitude }
      : null;

  return (
    <div className="property-details-page">
      {isLoading && <p>{t("properties.details.loading")}</p>}
      {!isLoading && error === "notFound" && (
        <p role="alert">{t("properties.details.notFound")}</p>
      )}
      {!isLoading && error === "generic" && (
        <p role="alert">{t("properties.details.error")}</p>
      )}

      {!isLoading && !error && property && (
        <>
          <header className="property-details-page__header">
            <div>
              <h1>{property.title ?? "—"}</h1>
              <p className="property-details-page__subtitle">
                {[
                  property.propertyType && t(`properties.type.${property.propertyType}`),
                  [address?.city, address?.country].filter(Boolean).join(", "),
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </p>
            </div>
            {property.status && (
              <span
                className={`status-badge status-badge--${property.status.toLowerCase()}`}
              >
                {t(`properties.status.${property.status}`)}
              </span>
            )}
          </header>

          {property.imageUrls && property.imageUrls.length > 0 ? (
            <div className="property-details-page__gallery">
              {property.imageUrls.map((url, index) => (
                <img
                  key={url}
                  src={url}
                  alt={t("properties.images.alt", { index: index + 1 })}
                />
              ))}
            </div>
          ) : (
            <p className="property-details-page__muted">{t("properties.images.empty")}</p>
          )}

          <div className="property-details-page__columns">
            <dl className="property-details-page__details">
              <dt>{t("properties.details.fields.capacity")}</dt>
              <dd>
                {t("properties.details.capacity", {
                  guests: property.maxGuests ?? 0,
                  bedrooms: property.bedrooms ?? 0,
                  beds: property.beds ?? 0,
                  bathrooms: property.bathrooms ?? 0,
                })}
              </dd>

              {property.surfaceM2 !== undefined && property.surfaceM2 !== null && (
                <>
                  <dt>{t("properties.details.fields.surface")}</dt>
                  <dd>{t("properties.details.surface", { value: property.surfaceM2 })}</dd>
                </>
              )}

              <dt>{t("properties.details.fields.nightlyPrice")}</dt>
              <dd>{formatPrice(property.nightlyPriceAmount, property.priceCurrency)}</dd>

              <dt>{t("properties.details.fields.cleaningFee")}</dt>
              <dd>{formatPrice(property.cleaningFeeAmount, property.priceCurrency)}</dd>

              <dt>{t("properties.details.fields.minNights")}</dt>
              <dd>{property.minNights ?? 1}</dd>

              <dt>{t("properties.details.fields.checkInOut")}</dt>
              <dd>
                {t("properties.details.checkInOut", {
                  checkIn: property.checkInTime || "—",
                  checkOut: property.checkOutTime || "—",
                })}
              </dd>

              <dt>{t("properties.details.fields.address")}</dt>
              <dd>
                {[address?.street, address?.postalCode, address?.city, address?.country]
                  .filter(Boolean)
                  .join(", ") || "—"}
              </dd>

              <dt>{t("properties.details.fields.amenities")}</dt>
              <dd>
                {amenityGroups.length > 0
                  ? amenityGroups.map((group) => (
                      <div key={group.code} className="property-details-page__amenity-group">
                        <strong>{group.label}</strong>{" "}
                        {group.amenities.map((amenity) => amenity.label).join(", ")}
                      </div>
                    ))
                  : property.amenities && property.amenities.length > 0
                    ? property.amenities.map(amenityCatalog.labelOf).join(", ")
                    : "—"}
              </dd>

              {property.description && (
                <>
                  <dt>{t("properties.details.fields.description")}</dt>
                  <dd className="property-details-page__text">{property.description}</dd>
                </>
              )}

              {property.houseRules && (
                <>
                  <dt>{t("properties.details.fields.houseRules")}</dt>
                  <dd className="property-details-page__text">{property.houseRules}</dd>
                </>
              )}
            </dl>

            <div className="property-details-page__map">
              <LocationPicker value={location} height={280} />
            </div>
          </div>

          <div className="property-details-page__bookings">
            <BookingsPanel
              propertyId={id}
              headerAction={
                <Link to={`/properties/${id}/bookings/new`}>
                  {t("properties.details.blockDatesLink")}
                </Link>
              }
            />
          </div>

          <div className="property-details-page__bookings">
            <CalendarSyncPanel propertyId={id} />
          </div>

          <div className="property-details-page__bookings">
            <PropertyReviewsPanel propertyId={id} />
          </div>
        </>
      )}

      <div className="property-details-page__actions">
        {!isLoading && !error && property && (
          <Link to={`/properties/${id}/edit`}>{t("properties.details.editLink")}</Link>
        )}
        <Link to={propertyListPath(session.role, property?.companyId)}>
          {t("properties.details.backLink")}
        </Link>
      </div>
    </div>
  );
}

export default PropertyDetailsPage;
